'use client'

import StartPageFooter from '@components/app/TrialApply/ApplyStartPage/StartPageFooter'
import TrialCardRevamped from '@components/app/TrialApply/ApplyStartPage/TrialCardRevamped'
import ExitIntentModal from '@components/app/TrialApply/ExitIntentModal/index.client'
import {
  ApplyFlowSection,
  ApplyFlowSectionScore,
  trackApplyFlowStepViewed,
} from '@components/app/TrialApply/trackApplyFlowStepViewed'
import { Icon } from '@components/primitives/Icon'
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck'
import { faClipboardListCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardListCheck'
import { faPhone } from '@fortawesome/pro-regular-svg-icons/faPhone'
import { faMoneyBill } from '@fortawesome/pro-solid-svg-icons/faMoneyBill'
import { formatMoneyFromCents } from '@lib/money/formatMoneyFromCents'
import capitalizeFirstWord from '@lib/utilities/text/capitalizeFirstWord'
import { type getClinicalTrialForGetStartedPage } from '@modules/trials/helpers/fragments/clinicalTrialGetStartedFragment'
import { getPreferredTrialTitleForAlgoliaTrial } from '@modules/trials/helpers/getPreferredTrialTitle'
import { getPrimaryConditionFromTrial } from '@modules/trials/helpers/getPrimaryConditionFromTrial'
import { useEffect } from 'react'

const steps = [
  {
    iconRef: faClipboardListCheck,
    title: 'Fill-in a few details and create your profile',
  },
  {
    iconRef: faCircleCheck,
    title: "We'll verify you meet the study requirements",
  },
  {
    iconRef: faPhone,
    title: 'Book a screening appointment with the study team',
  },
]

const TrialTimelineStepper = () => {
  return (
    <div className='rounded-lg border-[1.5px] border-primary100 p-6'>
      <div className='flex flex-col'>
        {steps.map((step, index) => (
          <div className='flex' key={step.title}>
            {/* Left column with icon and connector line */}
            <div className='flex flex-col items-center '>
              {/* Icon container */}
              <div className='flex items-center justify-center bg-primary50 p-3'>
                <Icon className='size-4 text-primary500' icon={step.iconRef} />
              </div>

              {/* Connector line (except for last item) */}
              {index < steps.length - 1 && (
                <div className='h-8 w-0.5 bg-primary200 md:h-10'></div>
              )}
            </div>

            {/* Right column with text */}
            <div className='ml-4 md:mt-2'>
              <p className='text-sm font-medium text-neutral900 md:text-base'>
                {step.title}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const ReimbursementUpsell = <
  T extends { reimbursements: { amount: number }[] },
>({
  trial,
}: {
  trial: T
}) => {
  const reimbursementDetails = trial.reimbursements[0]

  const reimbursementText = reimbursementDetails
    ? `Up to ${formatMoneyFromCents(reimbursementDetails.amount)} for your time`
    : 'Compensation provided for your time'

  return (
    <div className='flex items-center gap-4 rounded-lg border-primary100 bg-primary50 p-6 md:px-10'>
      <Icon className='size-10 text-primary600' icon={faMoneyBill} />
      <p className='text-sm font-semibold md:text-base'>{reimbursementText}</p>
    </div>
  )
}

export default function ApplyStartPage({
  genericPage = false,
  trial,
}: {
  genericPage?: boolean
  trial: Awaited<ReturnType<typeof getClinicalTrialForGetStartedPage>>
}) {
  const primaryCondition = getPrimaryConditionFromTrial(trial)
  const trialTitle = genericPage
    ? ''
    : getPreferredTrialTitleForAlgoliaTrial(trial, primaryCondition)

  const [treatment, condition] = trialTitle.includes('for')
    ? trialTitle.split(' for ')
    : []

  useEffect(() => {
    const stepNumber = 1
    trackApplyFlowStepViewed({
      applyFlowOverallNumber:
        ApplyFlowSectionScore[ApplyFlowSection.TRIAL_APPLY] + stepNumber,
      applyFlowQuestionText: 'Contact this trial V2',
      applyFlowSectionStepNumber: stepNumber,
      applyFlowSectionTitle: ApplyFlowSection.TRIAL_APPLY,
    })
  }, [])

  return (
    <>
      <div className='flex size-full justify-center '>
        <div className='mx-8 md:mx-0 md:max-w-[70%]'>
          <div className='mb-4 text-2xl font-extrabold text-neutral900 md:mb-8 md:text-4xl'>
            <span className='hidden md:block'>
              {genericPage ? 'Apply to the Trial' : 'Apply to this Trial'}
            </span>
            <span className='block md:hidden'>
              {treatment && condition ? (
                <>
                  Apply to the{' '}
                  <span className='text-primary600'>
                    {capitalizeFirstWord(treatment)} for{' '}
                    {capitalizeFirstWord(condition)}
                  </span>{' '}
                  Trial
                </>
              ) : (
                `Apply to the ${trialTitle} Trial`
              )}
            </span>
          </div>
          <div className='flex gap-8'>
            <TrialCardRevamped
              condition={primaryCondition}
              trialTitle={trialTitle}
            />
            <div className='flex flex-col gap-4'>
              <p className='text-base font-bold text-neutral900'>
                See if you qualify in 3 minutes
              </p>

              <TrialTimelineStepper />
              <ReimbursementUpsell trial={trial} />
            </div>
          </div>
        </div>
      </div>

      <StartPageFooter trial={trial} />
      <ExitIntentModal />
    </>
  )
}
